/**
 * Il listino dei piani per la console (28/10/2026): prezzi ed etichette da `tier_definitions`, MAI scritti nel codice, e
 * la stima della spesa al mese di un'azienda con la regola dei posti misti.
 *
 * Prima stava dentro `TenantBillingTab.tsx`, mentre l'elenco delle aziende (`TenantsListPage.tsx`) stimava l'incasso
 * con prezzi scritti a mano rimasti a un listino vecchio (Base 19 €, Business 99 €, e il Free contato a 19 €). Trovato
 * dal controllo `scripts/piani-coerenza-verifica.mjs`: ora le due pagine leggono lo stesso listino con la stessa regola.
 *
 * Dal 02/11/2026 la stima è il CANONE COMPLETO (posti + app a persona + strumenti a canone), calcolato dalla regola
 * condivisa `@shared/abbonamento/canoneMensile`: prima contava solo i posti, e Fortezza risultava 824 € mentre Stripe
 * fatturava 992 €. Qui si leggono i tre listini che servono (`useListiniDelCanone`).
 */

import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import type { SeatTier } from '@shared/types';
import { POSTI, ePostoFuoriDallaScala } from '@shared/abbonamento/funzioniDelPosto';
import type { ListiniDelCanone, PrezzoAPosto } from '@shared/abbonamento/canoneMensile';
import { leggiListino } from './strumentiService';

/**
 * Posti acquistabili (posti misti, 2026-07-18): la scala in ordine crescente, poi quelli FUORI DALLA SCALA (02/11/2026,
 * Presenze: chi timbra soltanto). Il Free non è un posto. Lo stesso elenco del gestionale e del server (`POSTI`).
 */
export const SEAT_TIERS: readonly SeatTier[] = POSTI;

/**
 * L'ordine in cui la console mette i piani: la scala col suo `order`, i posti fuori dalla scala dopo (non hanno `order`:
 * prima del 02/11/2026 un documento senza `order` valeva 0, cioè finiva accanto al Free).
 */
export function ordineNelListino(id: string, order: number | undefined): number {
  if (ePostoFuoriDallaScala(id)) return Number.MAX_SAFE_INTEGER;
  return order ?? 0;
}

/** Sottoinsieme dei campi di `tier_definitions/{tier}` usati dalla console. */
export interface TierDefLite {
  id: string;
  order: number;
  label: string;
  price_per_seat_eur_monthly: number;
}

// Cache a livello di modulo: il listino cambia di rado, e le pagine della console lo chiedono più volte.
const TIER_DEFS_TTL_MS = 5 * 60 * 1000;
let tierDefsCache: { at: number; defs: TierDefLite[] } | null = null;
let tierDefsInFlight: Promise<TierDefLite[]> | null = null;

export async function fetchTierDefs(): Promise<TierDefLite[]> {
  if (tierDefsCache && Date.now() - tierDefsCache.at < TIER_DEFS_TTL_MS) return tierDefsCache.defs;
  if (tierDefsInFlight) return tierDefsInFlight;
  tierDefsInFlight = (async () => {
    try {
      const snap = await getDocs(collection(db, 'tier_definitions'));
      const defs = snap.docs
        .map((d) => {
          const data = d.data() as Omit<TierDefLite, 'id'>;
          return {
            id: d.id,
            order: ordineNelListino(d.id, data.order),
            label: data.label ?? d.id,
            price_per_seat_eur_monthly: data.price_per_seat_eur_monthly ?? 0,
          } satisfies TierDefLite;
        })
        .sort((a, b) => a.order - b.order);
      tierDefsCache = { at: Date.now(), defs };
      return defs;
    } finally {
      tierDefsInFlight = null;
    }
  })();
  return tierDefsInFlight;
}

/** Hook: listino dei piani da Firestore. `defs` null finché carica; `error` se fallisce. */
export function useTierDefs(origine: string): { defs: TierDefLite[] | null; error: boolean } {
  const [defs, setDefs] = useState<TierDefLite[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchTierDefs()
      .then((d) => { if (alive) setDefs(d); })
      .catch((err) => {
        console.error(`[${origine}] Errore caricamento tier_definitions`, err);
        if (alive) setError(true);
      });
    return () => { alive = false; };
  }, [origine]);
  return { defs, error };
}

/** App a persona di `app_addons`: chi le paga e quanto (campi usati dalla console). */
async function leggiPrezziDelleApp(): Promise<Record<string, PrezzoAPosto>> {
  const snap = await getDocs(collection(db, 'app_addons'));
  const prezzi: Record<string, PrezzoAPosto> = {};
  for (const d of snap.docs) {
    const dati = d.data() as { capability?: unknown; label?: unknown; price_per_seat_eur_monthly?: unknown };
    // Il webhook scrive i posti comprati per capability (`seat.app_quota_by_id`): la chiave è quella.
    const capability = typeof dati.capability === 'string' && dati.capability !== '' ? dati.capability : d.id;
    prezzi[capability] = {
      etichetta: typeof dati.label === 'string' && dati.label !== '' ? dati.label : d.id,
      eur_mese: typeof dati.price_per_seat_eur_monthly === 'number' ? dati.price_per_seat_eur_monthly : null,
    };
  }
  return prezzi;
}

/**
 * Hook: i tre listini del canone (piani, app a persona, strumenti a canone). `listini` null finché carica; `error` se
 * uno dei tre non si legge (allora la console non mostra una cifra a metà).
 */
export function useListiniDelCanone(origine: string): { listini: ListiniDelCanone | null; error: boolean } {
  const [listini, setListini] = useState<ListiniDelCanone | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.all([fetchTierDefs(), leggiPrezziDelleApp(), leggiListino()])
      .then(([piani, app, strumenti]) => {
        if (!alive) return;
        const perPiano: Record<string, PrezzoAPosto> = {};
        for (const p of piani) perPiano[p.id] = { etichetta: p.label, eur_mese: p.price_per_seat_eur_monthly };
        setListini({ piani: perPiano, app, strumenti });
      })
      .catch((err) => {
        console.error(`[${origine}] Errore caricamento dei listini del canone`, err);
        if (alive) setError(true);
      });
    return () => { alive = false; };
  }, [origine]);
  return { listini, error };
}
